import { ElementValidationResult, LevelDesignerElementBase } from "../element-base";
import { PolygonElement } from "../polygon-element";
import { FormControl, FormGroup, FormArray } from '@angular/forms';
import { ParkingRowElement } from "./parkingrow";
import { PropertyMetadata } from "../../properties/property-types";
import { RegisterElement } from "../element-decorator";
import { v4 as uuidv4 } from 'uuid';
import { IClonable } from '../interfaces/clonable';
import { ParkingSpaceTypesService } from 'src/app/Services/parking-space-types.service';
import { GuidanceLight } from "../devices/guidance-light";

@RegisterElement()
export class ParkingSpaceElement extends PolygonElement implements IClonable {
    private _name: string;
    layerIndex: number = 3;
    public cloneDistance: number = 0; // 4 meters between parking spaces
    public override TypeName: string = "ParkingSpace";
    public override ParentType: string | null = "ParkingRow";
    public override ParentFormGroupName: string | null = "Spaces";
    protected override OutlineColor: string = '#000000';
    protected override FillColor: string = 'rgba(0, 255, 0, 0.3)';
    private previousPoints: { x: number, y: number }[] = [];
    public currentRow: ParkingRowElement | null = null;
    private svgImage: HTMLImageElement | null = null;

    constructor(
        formGroup: FormGroup, 
        private row?: ParkingRowElement,
        private spaceTypesService?: ParkingSpaceTypesService
    ) {
        super(formGroup);
        
        // Ensure all required form controls exist
        if (!formGroup.contains('Name')) {
            formGroup.addControl('Name', new FormControl(''));
        }
        if (!formGroup.contains('width')) {
            formGroup.addControl('width', new FormControl(5)); // Default width
        }
        if (!formGroup.contains('length')) {
            formGroup.addControl('length', new FormControl(3)); // Default length
        }
        if (!formGroup.contains('ParkingSpaceTypeId')) {
            formGroup.addControl('ParkingSpaceTypeId', new FormControl(null));
        }
        if (!formGroup.contains('ChipId')) {
            formGroup.addControl('ChipId', new FormControl(null));
        }
        
        this._name = formGroup.get('Name')?.value ?? '';

        // Load space types if service is provided
        if (this.spaceTypesService) {
            // Subscribe to space types loaded event
            this.spaceTypesService.onSpaceTypesLoaded.subscribe(this.updateFillColorAndRedraw);


            // Start loading the space types
            this.spaceTypesService.ensureLoaded().catch(error => {
                console.error('Failed to load space types:', error);
            }).then(() => {
                this.updateFillColorAndRedraw();
            });


            
        }
    }

    private updateFillColorAndRedraw() {
        const typeId = this.formGroup.get('ParkingSpaceTypeId')?.value;
        if (typeId) {
            const spaceType = this.spaceTypesService?.getSpaceType(typeId);
            if (spaceType) {
                // Update the fill color immediately
                this.FillColor = spaceType.Color;
                // Request a single redraw
                this.requestRedraw();
            }
        }
    }
   

    public duplicate(fromSpace?: IClonable): IClonable {
        // Create a new form group for the duplicate with all properties
        const duplicateForm = new FormGroup({
            Name: new FormControl(this.getIncrementedName((fromSpace as ParkingSpaceElement)?._name)),
            PolygonPoints: new FormControl([]),
            width: new FormControl(this.formGroup.get('width')?.value ?? 5),
            length: new FormControl(this.formGroup.get('length')?.value ?? 3),
            ParkingSpaceTypeId: new FormControl(this.formGroup.get('ParkingSpaceTypeId')?.value)
        });

        // Create new element with the new form group and pass the service
        const duplicate = new ParkingSpaceElement(duplicateForm, this.row, this.spaceTypesService);
        duplicate.points = [...this.points];
        duplicate.updateForm();

        return duplicate;
    }

    private getIncrementedName(fromName?: string): string {
        const nameToIncrement = fromName || this.formGroup.get('Name')?.value || '';
        // Match any number at the end of the string
        const match = nameToIncrement.match(/^(.*?)(\d+)$/);
        
        if (match) {
            const prefix = match[1];
            const number = parseInt(match[2]);
            return `${prefix}${number + 1}`;
        }
        
        return nameToIncrement;
    }

    override clone(): ParkingSpaceElement {
        // Pass the service when cloning
        return new ParkingSpaceElement(this.formGroup, this.row, this.spaceTypesService);
    }

    public override default() : ParkingSpaceElement {
        this.points = [
          { x: 0, y: 0 },
          { x: 3, y: 0 }, 
          { x: 3, y: 5 },
          { x: 0, y: 5 }
        ];
        this._name = '1';
        this.formGroup.patchValue({
            Id: uuidv4(),
            Name: '',
            PolygonPoints: this.points.map(p => [p.x / 1000, p.y / 1000]),
        });
        return this;
    }

    override draw(ctx: CanvasRenderingContext2D, offsetX?: number, offsetY?: number): void {
        // Get the space type if available
        const typeId = this.formGroup.get('ParkingSpaceTypeId')?.value;

        if (typeId && this.spaceTypesService) {
            const spaceType = this.spaceTypesService.getSpaceType(typeId);
            
            if (spaceType) {
                // Use the type's color for fill
                this.FillColor = "#" + spaceType.Color;

                // Draw the base polygon first
                super.draw(ctx, offsetX, offsetY);

                // If there's a media ID, try to get the loaded image
                if (spaceType.MediaId) {
                    const loadedImage = this.spaceTypesService.getLoadedImage(spaceType.MediaId);
                    if (loadedImage && loadedImage.complete) {
                        // Draw the icon using the loaded image
                        this.drawWithIcon(ctx, loadedImage, offsetX, offsetY);
                    }
                }
            } else {
                // Reset to default color if no type found
                this.FillColor = 'rgba(0, 255, 0, 0.3)';
                super.draw(ctx, offsetX, offsetY);
            }
        } else {
            // Draw the base polygon with default color
            super.draw(ctx, offsetX, offsetY);
        }
    }

    private drawWithIcon(ctx: CanvasRenderingContext2D, image: HTMLImageElement, offsetX?: number, offsetY?: number): void {
        // Calculate the center of the parking space
        const bounds = this.getBoundingBox();
        const centerX = bounds.x + bounds.width / 2;
        const centerY = bounds.y + bounds.height / 2;

        // Calculate icon size (e.g., 1/3 of the smaller dimension)
        const iconSize = Math.min(bounds.width, bounds.height) / 3;

        // Draw the icon centered in the space
        ctx.drawImage(
            image,
            centerX - iconSize / 2,
            centerY - iconSize / 2,
            iconSize,
            iconSize
        );
    }

    override updateForm(): void {
        super.updateForm();
        // Clear previous points after successful update
        this.previousPoints = [];
    }

    public drawPreview(ctx: CanvasRenderingContext2D, offsetX: number, offsetY: number): void {
        // Save current context state
        ctx.save();
        
        // Set semi-transparent colors for preview
        this.OutlineColor = 'rgba(0, 0, 0, 0.5)';
        this.FillColor = 'rgba(0, 255, 0, 0.15)';
        
        // Translate context by offset
        ctx.translate(offsetX, offsetY);
        
        super.draw(ctx, offsetX, offsetY);
        // Draw using existing draw method
        this.draw(ctx);
        
        // Restore context state
        ctx.restore();
    }

    public override getPropertyMetadata(): PropertyMetadata[] {
        return [
            {
                formControlName: 'Name',
                displayName: 'Space Number',
                inputType: { type: 'text' },
                description: 'Unique identifier for this parking space',
                required: true,
                order: 1,
                category: 'General'
            },
            {
                displayName: 'Width',
                inputType: { 
                    type: 'dimension',
                    unit: 'meters'
                },
                order: 2,
                category: 'Dimensions',
                getValue: (element) => element.getWidth(),
                setValue: (element, value) => element.setWidth(value)
            },
            {
                displayName: 'Length',
                inputType: { 
                    type: 'dimension',
                    unit: 'meters'
                },
                order: 3,
                category: 'Dimensions',
                getValue: (element) => element.getHeight(),
                setValue: (element, value) => element.setHeight(value)
            },
            {
                displayName: 'Space Type',
                formControlName: 'ParkingSpaceTypeId',
                order: 4,
                inputType: {
                    type: 'api-select',
                    url: '/infrastructure/spacetypes',
                    idProperty: 'Id',
                    labelProperty: 'Name'
                },
                category: 'General'
            },
            {
                formControlName: 'ChipId',
                displayName: 'Chip ID',
                inputType: { 
                    type: 'searchable-select',
                    url: '/hardware/sensors/available/searches',
                    idProperty: 'ChipId',
                    labelProperty: 'ChipId',
                    minSearchLength: 2
                },
                order: 5,
                category: 'Hardware'
            }
        ];
    }

    override move(deltaX: number, deltaY: number): void {
        // Store current position before moving
        this.previousPoints = [...this.points];
        
        // Perform the move
        super.move(deltaX, deltaY);
    }

    private requestRedraw(): void {
        // Use the parent row to find the level designer
        if (this.row?.currentParentElement) {
            // The row's parent should be the level, which has the designer
            const level = this.row.currentParentElement;
            // Find the designer through the level's parent chain
            let current: LevelDesignerElementBase | null = level;
            while (current?.currentParentElement) {
                current = current.currentParentElement;
            }
            // The top-most parent should have access to the designer
            if (current && 'redraw' in current) {
                (current as any).redraw();
            }
        }
    }

    public override checkPropertiesValid(allElements : LevelDesignerElementBase[]): ElementValidationResult {
        const result: ElementValidationResult = super.checkPropertiesValid(allElements);

        // Check if any guidance lights reference this space

        if(this.formGroup.get('ChipId')?.value) {
            const spaceId = this.formGroup.get('Id')?.value;
            const guidanceLights = allElements.filter(e => e instanceof GuidanceLight) as GuidanceLight[];
            for (const light of guidanceLights) {
                const portAssignments = light.formGroup.get('PortAssignments') as FormArray;
                if (!portAssignments) continue;

                // Check each port assignment
                for (const portControl of portAssignments.controls) {
                    const assignedSpaceId = (portControl as FormGroup).get('ParkingSpaceId')?.value;
                    if (assignedSpaceId === spaceId) {
                        result.errors.push(`Space has a ChipId assigned but is also assigned to port ${(portControl as FormGroup).get('PortNumber')?.value} of guidance light ${light.formGroup.get('Name')?.value}`);
                    }
                }
            }
        }
        return result;
    }
}
